import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { UnidadeConsumidora, Negociacao } from '../../../types';
import { X, Zap, Activity, CheckCircle2, XCircle, AlertTriangle, TrendingDown, FileText, ExternalLink, MinusCircle, Info } from 'lucide-react';
import { calcularConsumoUc } from '../../../src/lib/analiseTecnicaConsumo';
import RegraCalculoConsumo from '../../../src/components/RegraCalculoConsumo';
import { propsFundoModal } from '../../../src/lib/backdropModal';

interface UcDetailsReadOnlyProps {
    isOpen: boolean;
    onClose: () => void;
    uc: UnidadeConsumidora;
    negociacao?: Negociacao;
}

export default function UcDetailsReadOnly({ isOpen, onClose, uc, negociacao }: UcDetailsReadOnlyProps) {
    const [gaugeAnimated, setGaugeAnimated] = useState(false);

    useEffect(() => {
        if (isOpen) {
            const t = setTimeout(() => setGaugeAnimated(true), 150);
            return () => clearTimeout(t);
        }
        setGaugeAnimated(false);
    }, [isOpen]);

    const dados = uc.dados_tecnicos_equatorial;
    const checks = dados?.checks || {};
    const history = dados?.historyTable || [];

    const calc = useMemo(() => {
        const validHistory = history.filter((r: any) => !r.semRegistro);

        // Mesma regra do modal de Análise Técnica (src/lib/analiseTecnicaConsumo.ts):
        // antes esta tela dividia a injeção só pelos meses com injeção > 0 e não
        // aplicava a regra pró-cliente, então mostrava outro número pra mesma UC.
        const consumo = calcularConsumoUc({
            historico: history,
            tipoLigacao: dados?.tipoFornecimento || uc.tipo_ligacao,
            possuiGD: checks.possuiGD,
        });
        const { avgConsumo, avgInjecao, avgCompensavel } = consumo;

        const delays = validHistory.map((r: any) => Number(r.delayDays) || 0);
        const totalDelays = delays.reduce((a: number, d: number) => a + d, 0);
        const avgDelay = validHistory.length > 0 ? Math.round(totalDelays / validHistory.length) : 0;

        const hasGrave = validHistory.some((r: any) => r.ocorrencia && r.ocorrencia.toLowerCase().match(/renegocia|revelia|parcelamento|corte/));

        let cls = 'Excelente';
        if (hasGrave || avgDelay > 60) cls = 'Calote';
        else if (avgDelay > 30) cls = 'Péssimo';
        else if (avgDelay > 15) cls = 'Ruim';
        else if (avgDelay > 5) cls = 'Médio';
        else if (avgDelay > 0) cls = 'Bom';

        let ucStatus = 'Aprovada';
        let ucReasons: string[] = [];
        if (checks.injetando && !checks.distratoRecebido) { ucStatus = 'Reprovada'; ucReasons.push('Recebendo injeção sem documento de distrato.'); }
        if (cls === 'Calote' || cls === 'Péssimo') { ucStatus = 'Reprovada'; ucReasons.push('Classificação de pagamento inaceitável.'); }
        else if (cls === 'Ruim' || cls === 'Médio') { ucStatus = 'Pendente'; ucReasons.push('Histórico de risco médio/alto.'); }
        
        if ((dados?.grupo || uc.grupo_tarifario) === 'A (Média/Alta Tensão)') {
            ucReasons.push('Unidades do Grupo A demandam análise específica.');
        }

        if (avgCompensavel < 500 && !checks.consumo500) { ucStatus = 'Reprovada'; ucReasons.push('Consumo compensável abaixo de 500 kWh (Exceção justificada).'); }

        return { avgConsumo, avgInjecao, avgCompensavel, avgDelay, cls, ucStatus, ucReasons, consumo };
    }, [history, checks, dados, uc]);

    const totalCalc = useMemo(() => {
        if (!negociacao || !negociacao.unidades_consumidoras) return calc.avgConsumo;
        let sum = 0;
        const ucs: any[] = negociacao.unidades_consumidoras;
        ucs.forEach((u: any) => {
            if (u.id === uc.id) {
                sum += calc.avgConsumo;
            } else {
                sum += u.consumo_medio_kwh || 0;
            }
        });
        return sum;
    }, [negociacao, calc.avgConsumo, uc.id]);

    const vendorMismatch = negociacao?.consumo_medio_kwh !== undefined && Math.abs(negociacao.consumo_medio_kwh - totalCalc) > 100;

    // Per-UC divergence: UC's registered consumo vs 12-month calculated average
    const ucRegisteredConsumo = uc.consumo_medio_kwh ?? 0;
    const ucCalcConsumo = calc.avgConsumo;
    const ucConsumoDivergence = Math.abs(ucRegisteredConsumo - ucCalcConsumo);
    const hasUcDivergence = ucCalcConsumo > 0 && ucConsumoDivergence > 50;


    const clsColor = (c: string) => {
        if (c === 'Excelente' || c === 'Bom') return 'text-emerald-600';
        if (c === 'Médio') return 'text-amber-600';
        return 'text-red-600';
    };

    if (!isOpen) return null;

    const KPI = ({ label, value, accent }: { label: string; value: React.ReactNode; accent?: string }) => (
        <div className="bg-white dark:bg-slate-800/60 px-4 py-3 rounded-xl border border-slate-200/80 dark:border-slate-700/60 shadow-sm">
            <div className="text-[9px] font-bold text-slate-400 uppercase tracking-[0.12em] mb-1">{label}</div>
            <div className={`text-sm font-black ${accent || 'text-slate-800 dark:text-slate-100'} tabular-nums`}>{value}</div>
        </div>
    );

    /** Check item that respects semantic meaning: some checks are BAD when true */
    const CheckItem = ({ label, value, dangerWhenTrue }: { label: string; value?: boolean; dangerWhenTrue?: boolean }) => {
        const isUndefined = value === undefined;
        const isTrue = value === true;
        const isFalse = value === false;
        
        // Determine visual state
        let bgClass = '';
        let textClass = '';
        let icon: React.ReactNode;

        if (isUndefined) {
            // Not filled
            bgClass = 'border-slate-200 dark:border-slate-700/60 bg-slate-50/50 dark:bg-slate-800/30';
            textClass = 'text-slate-400 dark:text-slate-500';
            icon = <MinusCircle size={14} className="text-slate-300 dark:text-slate-600 shrink-0" />;
        } else if (dangerWhenTrue && isTrue) {
            // True = bad (e.g., "possui GD", "recebendo benefício", "injetando energia")
            bgClass = 'border-red-200/80 bg-red-50/60 dark:bg-red-900/10';
            textClass = 'text-red-700 dark:text-red-400';
            icon = <AlertTriangle size={14} className="text-red-500 shrink-0" />;
        } else if (dangerWhenTrue && isFalse) {
            // False = good for danger items
            bgClass = 'border-emerald-200/80 bg-emerald-50/60 dark:bg-emerald-900/10';
            textClass = 'text-emerald-700 dark:text-emerald-400';
            icon = <CheckCircle2 size={14} className="text-emerald-500 shrink-0" />;
        } else if (isTrue) {
            // True = good (normal checks)
            bgClass = 'border-emerald-200/80 bg-emerald-50/60 dark:bg-emerald-900/10';
            textClass = 'text-emerald-700 dark:text-emerald-400';
            icon = <CheckCircle2 size={14} className="text-emerald-500 shrink-0" />;
        } else {
            // False = bad (normal checks)
            bgClass = 'border-red-200/80 bg-red-50/60 dark:bg-red-900/10';
            textClass = 'text-red-700 dark:text-red-400';
            icon = <XCircle size={14} className="text-red-500 shrink-0" />;
        }

        const valueLabel = isUndefined ? 'Não preenchido' : isTrue ? 'Sim' : 'Não';

        return (
            <div className={`flex items-center justify-between gap-2.5 p-2.5 rounded-xl border text-sm font-medium ${bgClass}`}>
                <div className="flex items-center gap-2.5">
                    {icon}
                    <span className={textClass}>{label}</span>
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-wider ${isUndefined ? 'text-slate-400' : textClass}`}>{valueLabel}</span>
            </div>
        );
    };

    // Check if faturaUrl is a valid persistent URL (not a blob URL)
    const faturaUrl = dados?.faturaUrl;
    const isValidFaturaUrl = faturaUrl && typeof faturaUrl === 'string' && !faturaUrl.startsWith('blob:');

    return createPortal(
        <div className="fixed inset-0 z-[100001] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" {...propsFundoModal(() => onClose())}>
            <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-6xl max-h-[90vh] shadow-2xl overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-secondary/10 rounded-xl border border-secondary/20">
                            <Zap size={18} className="text-secondary" />
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h3 className="text-base font-bold text-slate-800 dark:text-slate-200">Análise Técnica — UC {uc.numero_uc}</h3>
                                {uc.outro_numero_uc && (
                                    <span className="text-[10px] font-bold text-slate-500 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded tracking-wider uppercase">
                                        {uc.outro_numero_uc}
                                    </span>
                                )}
                            </div>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.12em]">
                                {dados?.titularidade || uc.titular_fatura || uc.nome_cliente || '—'}
                                {dados?.novoNumeroUc && <span className="ml-2 text-primary">• Nova UC: {dados.novoNumeroUc}</span>}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl text-slate-400 hover:text-red-500 transition-all">
                        <X size={18} />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-slate-50/30 dark:bg-slate-900/50">
                    {/* KPIs */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        <KPI label="Consumo Médio" value={`${calc.avgConsumo} kWh`} accent="text-primary" />
                        {checks.possuiGD && <KPI label={calc.consumo.mesesComInjecao < calc.consumo.mesesBase ? `Injeção Média (${calc.consumo.mesesComInjecao}/${calc.consumo.mesesBase} meses)` : 'Injeção Média'} value={`${calc.avgInjecao} kWh`} accent="text-amber-600" />}
                        <KPI label="Compensável" value={`${calc.avgCompensavel} kWh`} accent="text-emerald-600" />
                        <KPI label="Atraso Médio" value={`${calc.avgDelay} dias`} accent={calc.avgDelay > 15 ? 'text-red-500' : calc.avgDelay > 0 ? 'text-amber-500' : 'text-emerald-600'} />
                        <KPI label="Classificação" value={calc.cls} accent={clsColor(calc.cls)} />
                    </div>

                    {/* De onde saem os kWh acima — mesma explicação do modal de análise */}
                    <div className="bg-white dark:bg-slate-900/80 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-sm overflow-hidden">
                        <RegraCalculoConsumo calculo={calc.consumo} className="border-b-0" />
                    </div>

                    {/* Dados Técnicos */}
                    <div className="bg-white dark:bg-slate-900/80 rounded-2xl border border-slate-200/80 dark:border-slate-800 p-5 shadow-sm">
                        <h4 className="text-xs font-bold text-slate-400 uppercase tracking-[0.12em] mb-4">Dados Técnicos — Equatorial</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div><span className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Titularidade</span><span className="font-bold text-slate-800 dark:text-slate-200">{dados?.titularidade || uc.titular_fatura || uc.nome_cliente || '—'}</span></div>
                            <div><span className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Unidade Consumidora</span><span className="font-bold text-slate-800 dark:text-slate-200">{uc.numero_uc || '—'} {uc.outro_numero_uc && <span className="text-[10px] text-slate-500 bg-slate-100 px-1 rounded ml-1">{uc.outro_numero_uc}</span>}</span></div>
                            {dados?.novoNumeroUc && <div><span className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Nova UC (Cemig)</span><span className="font-bold text-slate-800 dark:text-slate-200">{dados.novoNumeroUc}</span></div>}
                            <div><span className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Grupo Tarifário</span><span className="font-bold text-slate-800 dark:text-slate-200">{dados?.grupo || uc.grupo_tarifario || '—'}</span></div>
                            <div><span className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Tipo Ligação</span><span className="font-bold text-slate-800 dark:text-slate-200">{dados?.tipoFornecimento || uc.tipo_ligacao || '—'}</span></div>
                            <div><span className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Data Leitura</span><span className="font-bold text-slate-800 dark:text-slate-200">{dados?.dataLeitura || '—'}</span></div>
                            <div><span className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Apresentação</span><span className="font-bold text-slate-800 dark:text-slate-200">{dados?.dataApresentacao || '—'}</span></div>
                            <div><span className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Vencimento Pad.</span><span className="font-bold text-slate-800 dark:text-slate-200">{dados?.dataVencimento || '—'}</span></div>
                            <div><span className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Processamento</span><span className="font-bold text-slate-800 dark:text-slate-200">{dados?.dataProcessamento || '—'}</span></div>
                            {negociacao?.tipo_cliente === 'PF' && (
                                <div><span className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Data de Nascimento</span><span className="font-bold text-slate-800 dark:text-slate-200">{uc.data_nascimento_cliente ? new Date(uc.data_nascimento_cliente + 'T12:00:00').toLocaleDateString('pt-BR') : '—'}</span></div>
                            )}
                        </div>
                    </div>

                    {/* Status Banner */}
                    {(calc.ucStatus !== 'Aprovada' || vendorMismatch || hasUcDivergence) && (
                        <div className="flex flex-col gap-3">
                            {calc.ucStatus !== 'Aprovada' && (
                                <div className={`p-4 rounded-xl border flex flex-col gap-2 ${calc.ucStatus === 'Reprovada' ? 'bg-red-50/80 border-red-200 dark:bg-red-900/10 dark:border-red-800/40' : 'bg-amber-50/80 border-amber-200 dark:bg-amber-900/10 dark:border-amber-800/40'}`}>
                                    <div className="flex items-center gap-2">
                                        {calc.ucStatus === 'Reprovada' ? <XCircle size={16} className="text-red-600" /> : <AlertTriangle size={16} className="text-amber-600" />}
                                        <span className={`font-bold text-sm ${calc.ucStatus === 'Reprovada' ? 'text-red-800' : 'text-amber-800'}`}>Status: {calc.ucStatus}</span>
                                    </div>
                                    <ul className="list-disc list-inside text-sm ml-1 space-y-0.5">
                                        {calc.ucReasons.map((r, i) => <li key={i} className={calc.ucStatus === 'Reprovada' ? 'text-red-700' : 'text-amber-700'}>{r}</li>)}
                                    </ul>
                                </div>
                            )}
                            
                            {vendorMismatch && (
                                <div className="p-4 rounded-xl border bg-orange-50/80 border-orange-200 dark:bg-orange-900/10 dark:border-orange-800/40 flex items-start gap-3">
                                    <AlertTriangle size={18} className="text-orange-600 shrink-0 mt-0.5" />
                                    <div>
                                        <h4 className="font-bold text-sm text-orange-800 dark:text-orange-400">Divergência de Consumo Global</h4>
                                        <p className="text-sm text-orange-700 dark:text-orange-500 mt-1">O consumo Global preenchido na proposta ({negociacao?.consumo_medio_kwh} kWh) está diferente da soma das UCs conferidas ({totalCalc} kWh).</p>
                                    </div>
                                </div>
                            )}

                            {hasUcDivergence && (
                                <div className="p-4 rounded-xl border bg-amber-50/80 border-amber-200 dark:bg-amber-900/10 dark:border-amber-800/40 flex items-start gap-3">
                                    <TrendingDown size={18} className="text-amber-600 shrink-0 mt-0.5" />
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h4 className="font-bold text-sm text-amber-800 dark:text-amber-400">Divergência de Consumo — UC {uc.numero_uc}</h4>
                                            {uc.outro_numero_uc && (
                                                <span className="text-[10px] font-bold text-amber-700 bg-amber-100 dark:bg-amber-900/50 px-1.5 rounded uppercase">
                                                    {uc.outro_numero_uc}
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-sm text-amber-700 dark:text-amber-500 mt-1">
                                            <span className="font-bold">Registrado pelo vendedor:</span> {ucRegisteredConsumo.toLocaleString('pt-BR')} kWh
                                            <span className="mx-2">→</span>
                                            <span className="font-bold">Média real (12 meses):</span> {ucCalcConsumo.toLocaleString('pt-BR')} kWh
                                            <span className="ml-2 px-2 py-0.5 rounded-md text-xs font-bold bg-amber-200/60 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300">
                                                {ucCalcConsumo > ucRegisteredConsumo ? '+' : ''}{(ucCalcConsumo - ucRegisteredConsumo).toLocaleString('pt-BR')} kWh
                                            </span>
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Fatura Uploaded visibility */}
                    {faturaUrl && (
                        <div className={`border rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${isValidFaturaUrl ? 'bg-indigo-50/50 dark:bg-indigo-900/10 border-indigo-200/60 dark:border-indigo-800/40' : 'bg-amber-50/50 dark:bg-amber-900/10 border-amber-200/60 dark:border-amber-800/40'}`}>
                            <div className="flex items-center gap-3">
                                <div className={`p-2 rounded-lg ${isValidFaturaUrl ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400' : 'bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400'}`}><FileText size={16} /></div>
                                <div>
                                    <p className={`text-sm font-bold ${isValidFaturaUrl ? 'text-indigo-900 dark:text-indigo-300' : 'text-amber-900 dark:text-amber-300'}`}>Fatura Atualizada pela Análise</p>
                                    <p className={`text-xs ${isValidFaturaUrl ? 'text-indigo-500' : 'text-amber-500'} max-w-[300px] truncate`}>
                                        {isValidFaturaUrl ? (dados?.faturaNome || 'Documento visualizável') : 'Link temporário expirado — fatura precisa ser reenviada'}
                                    </p>
                                </div>
                            </div>
                            {isValidFaturaUrl && (
                                <a href={faturaUrl} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 flex justify-center items-center gap-1.5 text-xs font-bold bg-white dark:bg-slate-800 border border-indigo-200 dark:border-indigo-700/50 rounded-lg text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors shadow-sm">
                                    <ExternalLink size={12} /> Abrir Fatura
                                </a>
                            )}
                        </div>
                    )}

                    {/* Checks — with semantic color differentiation */}
                    <div className="bg-white dark:bg-slate-900/80 rounded-2xl border border-slate-200/80 dark:border-slate-800 p-5 shadow-sm">
                        <h4 className="text-xs font-bold text-slate-400 uppercase tracking-[0.12em] mb-4">Conferências e Regras de Negócio</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                            <CheckItem label="Fatura atualizada na Equatorial?" value={checks.atualizada} />
                            <CheckItem label="A Unidade é Grupo B?" value={checks.grupoB} />
                            <CheckItem label="Consumo Compensável > 500 kWh?" value={checks.consumo500} />
                            <CheckItem label="A Unidade possui GD instalada?" value={checks.possuiGD} dangerWhenTrue />
                            <CheckItem label="Recebendo algum Benefício (Baixa Renda, etc)?" value={checks.beneficio} dangerWhenTrue />
                            <CheckItem label="Há terceiros injetando energia na UC?" value={checks.injetando} dangerWhenTrue />
                            <CheckItem label="Titular confere exatamente com o termo?" value={checks.mesmoTitular} />
                            {checks.injetando && <CheckItem label="Documento de distrato de GD cancelado recebido?" value={checks.distratoRecebido} />}
                        </div>
                        {/* Show explanations if any */}
                        {(checks.explicacaoGrupoB || checks.explicacaoConsumo500 || checks.explicacaoGD) && (
                            <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 space-y-1.5">
                                {checks.explicacaoGrupoB && (
                                    <div className="flex items-start gap-2 text-xs"><Info size={12} className="text-amber-500 shrink-0 mt-0.5" /><span className="text-slate-600 dark:text-slate-400"><strong className="text-slate-700 dark:text-slate-300">Justificativa Grupo B:</strong> {checks.explicacaoGrupoB}</span></div>
                                )}
                                {checks.explicacaoConsumo500 && (
                                    <div className="flex items-start gap-2 text-xs"><Info size={12} className="text-amber-500 shrink-0 mt-0.5" /><span className="text-slate-600 dark:text-slate-400"><strong className="text-slate-700 dark:text-slate-300">Justificativa Consumo &lt;500:</strong> {checks.explicacaoConsumo500}</span></div>
                                )}
                                {checks.explicacaoGD && (
                                    <div className="flex items-start gap-2 text-xs"><Info size={12} className="text-amber-500 shrink-0 mt-0.5" /><span className="text-slate-600 dark:text-slate-400"><strong className="text-slate-700 dark:text-slate-300">Detalhes GD:</strong> {checks.explicacaoGD}</span></div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Histórico 12 meses */}
                    {history.length > 0 && (
                        <div className="bg-white dark:bg-slate-900/80 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-sm overflow-hidden">
                            <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-slate-100 dark:border-slate-800">
                                <Activity size={14} className="text-primary" />
                                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-[0.12em]">Histórico de Consumo e Pagamento (12 Meses)</h4>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm text-left whitespace-nowrap">
                                    <thead className="bg-slate-50/80 dark:bg-slate-800/40 border-b border-slate-100 dark:border-slate-800">
                                        <tr>
                                            <th className="p-3 text-center text-[10px] font-bold text-slate-400 uppercase tracking-wider">Mês</th>
                                            <th className="p-3 text-right text-[10px] font-bold text-slate-400 uppercase tracking-wider">Consumo</th>
                                            {checks.possuiGD && <th className="p-3 text-right text-[10px] font-bold text-amber-500 uppercase tracking-wider bg-amber-50/30">Injeção</th>}
                                            <th className="p-3 text-center text-[10px] font-bold text-slate-400 uppercase tracking-wider">Vencimento</th>
                                            <th className="p-3 text-center text-[10px] font-bold text-slate-400 uppercase tracking-wider">Pagamento</th>
                                            <th className="p-3 text-center text-[10px] font-bold text-slate-400 uppercase tracking-wider">Atraso</th>
                                            <th className="p-3 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Ocorrência</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50 dark:divide-slate-800/60">
                                        {history.map((row: any, idx: number) => {
                                            const isSemRegistro = row.semRegistro === true;
                                            const isEmAberto = row.emAberto === true;
                                            
                                            if (isSemRegistro) {
                                                // N/A row — clearly mark it
                                                return (
                                                    <tr key={idx} className="bg-slate-50/60 dark:bg-slate-800/20">
                                                        <td className="p-2.5 font-bold text-slate-400 text-center text-xs">{row.month || '—'}</td>
                                                        <td colSpan={checks.possuiGD ? 6 : 5} className="p-2.5 text-center">
                                                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-400 text-[10px] font-bold uppercase tracking-wider">
                                                                <MinusCircle size={10} /> Sem registro / N/A
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            }

                                            return (
                                                <tr key={idx} className="hover:bg-primary/[0.02] transition-colors">
                                                    <td className="p-2.5 font-bold text-slate-500 text-center text-xs">{row.month || '—'}</td>
                                                    <td className="p-2.5 text-right font-bold text-slate-700 dark:text-slate-200 tabular-nums">{row.consumo || 0}</td>
                                                    {checks.possuiGD && <td className="p-2.5 text-right font-bold text-amber-600 tabular-nums bg-amber-50/20">{row.injecao || 0}</td>}
                                                    <td className="p-2.5 text-center text-slate-600 text-xs">{row.vencimento ? new Date(row.vencimento).toLocaleDateString('pt-BR') : '—'}</td>
                                                    <td className="p-2.5 text-center text-xs">
                                                        {isEmAberto ? (
                                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 font-bold text-[10px] uppercase">
                                                                <AlertTriangle size={10} /> Em Aberto
                                                            </span>
                                                        ) : row.pagamento ? (
                                                            <span className="text-slate-600">{new Date(row.pagamento).toLocaleDateString('pt-BR')}</span>
                                                        ) : (
                                                            <span className="text-slate-300 dark:text-slate-600 italic">—</span>
                                                        )}
                                                    </td>
                                                    <td className="p-2.5 text-center">
                                                        <span className={`inline-block px-2 py-0.5 rounded-md font-bold text-xs ${row.delayDays > 15 ? 'bg-red-100 text-red-700' : row.delayDays > 5 ? 'bg-amber-100 text-amber-700' : row.delayDays > 0 ? 'bg-slate-100 text-slate-700' : 'text-emerald-600'}`}>
                                                            {row.delayDays || 0}
                                                        </span>
                                                    </td>
                                                    <td className="p-2.5 text-sm text-slate-600">{row.ocorrencia || '—'}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Classificação Pagamento */}
                    <div className="bg-white dark:bg-slate-900/80 rounded-2xl border border-slate-200/80 dark:border-slate-800 p-5 shadow-sm">
                        <div className="flex flex-col md:flex-row gap-6 items-center">
                            <div className="flex-1">
                                <h4 className="font-bold text-slate-800 dark:text-slate-200 mb-2">Classificação de Pagamento</h4>
                                <p className="text-sm text-slate-500 mb-4">Média de <span className="font-bold text-slate-700 dark:text-slate-200">{calc.avgDelay} dias</span> de atraso.</p>
                                <div className="space-y-1.5">
                                    {[
                                        { label: 'Excelente – paga antes ou no vencimento', cls: 'Excelente' },
                                        { label: 'Bom – até 5 dias após vencimento', cls: 'Bom' },
                                        { label: 'Médio – até 15 dias após vencimento', cls: 'Médio' },
                                        { label: 'Ruim – até 30 dias após vencimento', cls: 'Ruim' },
                                        { label: 'Péssimo – até 60 dias após vencimento', cls: 'Péssimo' },
                                        { label: 'Calote – >60 dias ou renegociação/revelia', cls: 'Calote' },
                                    ].map(item => (
                                        <div key={item.cls} className={`flex items-center gap-3 p-2.5 rounded-xl border transition-all ${calc.cls === item.cls ? 'bg-primary/8 border-primary/30 shadow-sm' : 'bg-slate-50/50 border-transparent opacity-40'}`}>
                                            <div className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center shrink-0 ${calc.cls === item.cls ? 'border-primary bg-primary' : 'border-slate-300'}`}>
                                                {calc.cls === item.cls && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                                            </div>
                                            <span className={`text-sm font-semibold ${calc.cls === item.cls ? 'text-slate-800 dark:text-slate-100' : 'text-slate-500'}`}>{item.label}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="w-full md:w-56 flex flex-col items-center bg-gradient-to-b from-slate-50 to-white dark:from-slate-800/40 dark:to-slate-900/40 p-6 rounded-2xl border border-slate-200/80 dark:border-slate-800 text-center">
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.12em]">Média de Atraso</span>
                                <span className={`text-5xl font-black my-3 tabular-nums ${calc.avgDelay > 15 ? 'text-red-500' : calc.avgDelay > 0 ? 'text-amber-500' : 'text-primary'}`}>{calc.avgDelay}</span>
                                <span className="text-sm font-bold text-slate-400">dias corridos</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}
